import { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Building2, CheckCircle2, XCircle, Clock, CreditCard, Landmark, FileDown } from "lucide-react";

const fmtEur = (v) => `€ ${Number(v || 0).toFixed(2)}`;
const fmtDate = (s) => s ? new Date(s).toLocaleDateString("it-IT") : "—";

const STATO_LABEL = {
  in_attesa_autorizzazione: { l: "In attesa autorizzazione", c: "bg-amber-100 text-amber-800", icon: Clock },
  in_attesa_pagamento: { l: "In attesa pagamento", c: "bg-blue-100 text-blue-800", icon: CreditCard },
  pagato: { l: "Pagato · attivo", c: "bg-emerald-100 text-emerald-800", icon: CheckCircle2 },
  rifiutata: { l: "Rifiutata", c: "bg-red-100 text-red-700", icon: XCircle },
};

/** Iter122bh · Super_admin · Gestione richieste condomìni
 * Iter122bj · può essere embedded dentro la scheda studio con prop `tenantId` (nasconde header e filtra).
 */
export default function SuperAdminCondominiRichieste({ tenantId = null, compact = false }) {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [refCRO, setRefCRO] = useState({});

  const load = () => api.get("/superadmin/richieste-condominio", { params: tenantId ? { tenant_id: tenantId } : {} })
    .then((r) => setItems(r.data || []))
    .catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tenantId]);

  const autorizza = async (r) => {
    setBusy(true);
    try { await api.post(`/superadmin/richieste-condominio/${r.id}/autorizza`, {}); toast.success("Autorizzata · in attesa di pagamento"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Errore"); }
    setBusy(false);
  };
  const rifiuta = async (r) => {
    const motivo = window.prompt(`Motivo del rifiuto per "${r.condominio?.nome}":`);
    if (!motivo) return;
    setBusy(true);
    try { await api.post(`/superadmin/richieste-condominio/${r.id}/rifiuta`, { motivo }); toast.success("Rifiutata"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Errore"); }
    setBusy(false);
  };
  const marcaPagato = async (r) => {
    const ref = refCRO[r.id] || window.prompt("CRO bonifico / riferimento pagamento:");
    if (!ref) return;
    setBusy(true);
    try {
      await api.post(`/superadmin/richieste-condominio/${r.id}/marca-pagato`, { metodo: "bonifico", riferimento: ref });
      toast.success("Marcato come pagato · condominio attivato");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Errore"); }
    setBusy(false);
  };

  const scaricaPdf = async (r) => {
    try {
      const res = await api.get(`/superadmin/richieste-condominio/${r.id}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `nota-prorata-${r.id.slice(0,8)}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error("Errore scaricamento PDF"); }
  };

  return (
    <div className="space-y-3" data-testid="superadmin-condomini-richieste">
      {!compact && (
        <div className="flex items-center gap-2 mb-2">
          <Building2 className="w-5 h-5 text-brand" />
          <h3 className="font-display text-lg font-bold text-slate-800">Richieste aggiunta condomìni · piano per_condominio</h3>
        </div>
      )}
      {items.length === 0 && <Card className="p-3 text-center text-slate-400 text-sm">Nessuna richiesta.</Card>}
      {items.map((r) => {
        const S = STATO_LABEL[r.stato] || { l: r.stato, c: "bg-slate-100", icon: Clock };
        const Ic = S.icon;
        return (
          <Card key={r.id} className="p-4 border-2" data-testid={`sa-richiesta-${r.id}`}>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <div className="font-bold flex flex-wrap items-center gap-2">
                  {!compact && <><span className="text-slate-500 text-xs">{r.tenant_id}</span><span>·</span></>}
                  <span className="text-slate-800">{r.condominio?.nome}</span>
                  <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full inline-flex items-center gap-1 ${S.c}`}>
                    <Ic className="w-3 h-3" /> {S.l}
                  </span>
                </div>
                <div className="text-sm text-slate-500 mt-0.5">
                  {r.condominio?.indirizzo} · {r.condominio?.citta} {r.condominio?.cap}
                </div>
                <div className="text-xs text-slate-500 mt-1">
                  Richiesta il {fmtDate(r.created_at)} · Prorata: <b>{fmtEur(r.totale)}</b> ({r.giorni_utilizzo} gg × {fmtEur(r.prezzo_unit_annuo)}/anno + IVA {r.iva_pct}%)
                  {r.note_studio && <span className="italic block mt-1">"{r.note_studio}"</span>}
                  {r.data_pagamento && <span className="block mt-1 text-emerald-700">Pagato il {fmtDate(r.data_pagamento)} · {r.metodo_pagamento} {r.riferimento_pagamento ? `(${r.riferimento_pagamento})` : ""}</span>}
                  {r.rifiuto_motivo && <span className="block mt-1 text-red-600">Rifiuto: {r.rifiuto_motivo}</span>}
                </div>
              </div>
              <div className="flex flex-wrap gap-2 items-center">
                {r.stato === "in_attesa_autorizzazione" && (
                  <>
                    <Button size="sm" disabled={busy} onClick={() => autorizza(r)} data-testid={`sa-autorizza-${r.id}`} className="bg-emerald-600 hover:bg-emerald-700 h-9">
                      <CheckCircle2 className="w-4 h-4 mr-1" /> Autorizza
                    </Button>
                    <Button size="sm" variant="outline" disabled={busy} onClick={() => rifiuta(r)} data-testid={`sa-rifiuta-${r.id}`} className="h-9 text-red-600 border-red-300">
                      <XCircle className="w-4 h-4 mr-1" /> Rifiuta
                    </Button>
                  </>
                )}
                {r.stato === "in_attesa_pagamento" && (
                  <div className="flex flex-wrap gap-2 items-center">
                    <Input placeholder="CRO bonifico" data-testid={`sa-ref-${r.id}`}
                           value={refCRO[r.id] || ""}
                           onChange={(e) => setRefCRO((s) => ({ ...s, [r.id]: e.target.value }))}
                           className="h-9 w-40" />
                    <Button size="sm" disabled={busy} onClick={() => marcaPagato(r)} data-testid={`sa-marca-pagato-${r.id}`} className="bg-brand hover:bg-brand-dark h-9">
                      <Landmark className="w-4 h-4 mr-1" /> Marca pagato
                    </Button>
                  </div>
                )}
                <Button size="sm" variant="outline" onClick={() => scaricaPdf(r)} data-testid={`sa-scarica-pdf-${r.id}`} className="h-9">
                  <FileDown className="w-4 h-4 mr-1" /> PDF
                </Button>
              </div>
            </div>
          </Card>
        );
      })}
    </div>
  );
}
